import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, BookOpen, Pill, CalendarCheck, Receipt, Plus, Trash2, Check, Loader2,
  Link2, ExternalLink, Lock, Fingerprint, IdCard, LifeBuoy, Download, Clock, XCircle, Activity, ShieldCheck,
  Bell, Moon, Camera, FileDown, AlertTriangle, Luggage, Tag as TagIcon, Plane, Users,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VaultGate, { isUnlocked, lockVault, HIDDEN_LOCK_MS } from "../components/VaultGate";
import { AppTopBar } from "../components/AppScreen";

/**
 * My Traveler Wallet — one hub for four private travel modules:
 * 1) Passport & travel history archive
 * 2) Travel medication records
 * 3) Daily medication reminders (dose checklist)
 * 4) Trip receipts linked into the expense tracker (no double-counting)
 *
 * All data is scoped to the signed-in user. Files are stored in the shared
 * object-storage bucket — we only keep object keys here.
 */

const MODULES = [
  { id: "passports", icon: BookOpen, title: "Passport & ID Vault", color: "sky",
    desc: "Passports, driver's license & IDs. Locked with PIN + biometrics.", locked: true },
  { id: "bags", icon: Luggage, title: "My Bags — Packing Buddy", color: "indigo",
    desc: "Track each bag, tag number, owner and flight. Photos in one place." },
  { id: "medications", icon: Pill, title: "Travel Medication Records", color: "emerald",
    desc: "Prescriptions, pharmacy labels and doctor's letters for customs." },
  { id: "schedule", icon: CalendarCheck, title: "Daily Medication Reminders", color: "violet",
    desc: "Today's dose checklist with taken / not-taken notes." },
  { id: "receipts", icon: Receipt, title: "Trip Receipts & Expenses", color: "amber",
    desc: "Photograph receipts, add to your trip and expense tracker once." },
];

const TONES = {
  sky: { active: "border-sky-500 bg-sky-50", icon: "text-sky-600" },
  indigo: { active: "border-indigo-500 bg-indigo-50", icon: "text-indigo-600" },
  emerald: { active: "border-emerald-500 bg-emerald-50", icon: "text-emerald-600" },
  violet: { active: "border-violet-500 bg-violet-50", icon: "text-violet-600" },
  amber: { active: "border-amber-500 bg-amber-50", icon: "text-amber-600" },
};

export default function Wallet() {
  const { user, openLogin } = useAuth();
  const [tab, setTab] = useState("passports");
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [showGate, setShowGate] = useState(false);

  useEffect(() => { document.title = "My Traveler Wallet — My Social Pulse"; }, []);
  useEffect(() => { setVaultUnlocked(isUnlocked()); }, []);

  // Auto-lock: when the tab has been hidden for HIDDEN_LOCK_MS, wipe the
  // unlock flag so returning to the app requires PIN/biometrics again.
  const hiddenSinceRef = useRef(null);
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
      } else if (hiddenSinceRef.current && Date.now() - hiddenSinceRef.current >= HIDDEN_LOCK_MS) {
        lockVault();
        setVaultUnlocked(false);
        toast("Vault re-locked", { description: "You were away for over a minute." });
      }
      if (!document.hidden) hiddenSinceRef.current = null;
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const requestTab = (id) => {
    if (id === "passports" && !vaultUnlocked) { setShowGate(true); return; }
    setTab(id);
  };

  const onUnlocked = () => { setVaultUnlocked(true); setShowGate(false); setTab("passports"); };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="wallet-signin">
        <WalletIcon className="w-12 h-12 text-primary mx-auto mb-4" data-testid="wallet-signin-icon" />
        <h1 className="font-heading font-900 text-3xl mb-2">Sign in to open your Traveler Wallet</h1>
        <p className="text-muted-foreground mb-6">Your passport archive, medications and receipts are private to your account.</p>
        <button onClick={openLogin} className="px-6 py-3 bg-primary text-white font-700" data-testid="wallet-signin-btn">Sign in</button>
      </div>
    );
  }

  return (
    <div data-testid="wallet-page">
      <AppTopBar title="Traveler Wallet" />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <WalletIcon className="w-8 h-8 text-primary" />
        <h1 className="font-heading font-900 text-3xl sm:text-4xl">My Traveler Wallet</h1>
      </div>
      <p className="text-muted-foreground mb-6 max-w-2xl">
        Keep travel records together, remember daily essentials and track what you spend. Your records stay private to your account.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8" data-testid="wallet-tabs">
        {MODULES.map((m) => {
          const tone = TONES[m.color] || TONES.sky;
          const active = tab === m.id;
          return (
            <button key={m.id} onClick={() => requestTab(m.id)} data-testid={`wallet-tab-${m.id}`}
              className={"border p-3 text-left transition-colors " + (active ? tone.active : "border-zinc-200 hover:border-zinc-400")}>
              <div className="flex items-center justify-between mb-1">
                <m.icon className={"w-5 h-5 " + tone.icon} />
                {m.locked && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-700 text-sky-800 bg-sky-100 border border-sky-300 px-1">
                    <Lock className="w-3 h-3" /> {vaultUnlocked ? "Unlocked" : "Locked"}
                  </span>
                )}
              </div>
              <div className="font-700 text-sm leading-tight">{m.title}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{m.desc}</div>
            </button>
          );
        })}
      </div>

      {showGate && <VaultGate onUnlock={onUnlocked} onClose={() => setShowGate(false)} />}

      {tab === "passports" && (vaultUnlocked ? (
        <>
          <Passports />
          <VaultTools />
          <VaultNotificationPrefs />
          <VaultQuietHours />
          <VaultActivity />
        </>
      ) : (
        <div className="border-2 border-dashed border-sky-300 bg-sky-50/50 p-8 text-center" data-testid="wallet-passports-locked">
          <Lock className="w-10 h-10 text-sky-700 mx-auto mb-3" />
          <h3 className="font-heading font-900 text-xl mb-1">This vault is locked</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Your passport, driver's license and ID records are protected by a PIN plus optional Face ID / Touch ID. Auto-locks when hidden 60s+.
          </p>
          <button onClick={() => setShowGate(true)} data-testid="wallet-vault-unlock-cta"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-sky-600 text-white font-700 text-sm">
            <Fingerprint className="w-4 h-4" /> Unlock vault
          </button>
        </div>
      ))}
      {tab === "medications" && <Medications />}
      {tab === "schedule" && <Schedule />}
      {tab === "receipts" && <Receipts />}
      {tab === "bags" && <Bags />}

      <p className="mt-10 text-xs text-muted-foreground border-t border-zinc-200 pt-4">
        Digital copies are reference records — carry originals when required. Medication information does not replace destination permits.
      </p>
      </div>
    </div>
  );
}

/* ------------------------------- shared bits ------------------------------- */

const Field = ({ label, children }) => (
  <label className="block text-sm"><span className="font-600">{label}</span>{children}</label>
);
const TextIn = (props) => <input {...props} className="mt-1 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-primary" />;
const Area = (props) => <textarea rows={3} {...props} className="mt-1 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-primary" />;

async function refresh(setter, url, params) {
  try { const { data } = await api.get(url, { params }); setter(data.items || []); }
  catch { /* silent */ }
}

/* ------------------------------- Passports -------------------------------- */

function Passports() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [filter, setFilter] = useState("all"); // all | passport | drivers_license
  useEffect(() => { refresh(setItems, "/wallet/passports"); }, []);

  const save = async () => {
    if (!form.number || !form.country_of_issue) { toast.error("Number and country of issue are required"); return; }
    try {
      const method = form.id ? "patch" : "post";
      const url = form.id ? `/wallet/passports/${form.id}` : "/wallet/passports";
      await api[method](url, form);
      toast.success(form.document_kind === "drivers_license" ? "Driver's license saved" : "Passport saved");
      setForm(null); refresh(setItems, "/wallet/passports");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/wallet/passports/${id}`);
    refresh(setItems, "/wallet/passports");
  };

  const shown = items.filter((p) => filter === "all" || (p.document_kind || "passport") === filter);
  const kindLabel = (k) => k === "drivers_license" ? "Driver's License" : k === "national_id" ? "National ID" : "Passport";

  return (
    <section data-testid="wallet-passports">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h2 className="font-heading font-800 text-xl">Passports, Driver's Licenses & IDs</h2>
        </div>
        <div className="flex items-center gap-1">
          {[["all", "All"], ["passport", "Passports"], ["drivers_license", "Licenses"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`wallet-id-filter-${k}`}
              className={"text-xs px-2 py-1 border " + (filter === k ? "border-sky-500 bg-sky-50 text-sky-700 font-700" : "border-zinc-300 text-zinc-600")}>{l}</button>
          ))}
          <div className="w-px h-5 bg-zinc-200 mx-1" />
          <button onClick={() => setForm({ document_kind: "passport", number: "", country_of_issue: "", is_current: false, stamps: [] })}
            data-testid="wallet-passport-add" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 text-white text-sm font-700">
            <Plus className="w-4 h-4" /> Passport
          </button>
          <button onClick={() => setForm({ document_kind: "drivers_license", number: "", country_of_issue: "", is_current: true, stamps: [] })}
            data-testid="wallet-license-add" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-800 text-white text-sm font-700">
            <IdCard className="w-4 h-4" /> License
          </button>
        </div>
      </div>
      {shown.length === 0 && !form && <p className="text-sm text-muted-foreground">Nothing here yet. Add your passports and driver's licenses.</p>}
      <div className="space-y-3">
        {shown.map((p) => {
          const kind = p.document_kind || "passport";
          const isDL = kind === "drivers_license";
          return (
            <div key={p.id} className="border border-zinc-200 bg-white p-4" data-testid={`wallet-passport-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className={"text-xs font-mono uppercase " + (isDL ? "text-sky-900" : "text-sky-700")}>
                    {isDL ? <IdCard className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> : <BookOpen className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />}
                    {kindLabel(kind)} · {p.country_of_issue} {p.is_current && "· Current"}
                  </div>
                  <div className="font-700">#{p.number}</div>
                  <div className="text-xs text-muted-foreground">
                    Issued {p.date_of_issue || "—"} · Expires {p.date_of_expiry || "—"} · {p.place_of_issue || "—"}
                  </div>
                  {isDL && (p.license_class || p.endorsements || p.restrictions) && (
                    <div className="text-xs mt-1 text-zinc-700">
                      {p.license_class && <>Class <b>{p.license_class}</b>. </>}
                      {p.endorsements && <>Endorsements: {p.endorsements}. </>}
                      {p.restrictions && <>Restrictions: {p.restrictions}.</>}
                    </div>
                  )}
                  {p.notes && <p className="text-sm mt-1 text-zinc-700">{p.notes}</p>}
                  {p.stamps?.length > 0 && (
                    <ul className="mt-2 text-xs text-zinc-700 border-t border-zinc-100 pt-2 space-y-0.5">
                      {p.stamps.map((s, i) => (
                        <li key={i}>· <b>{s.country}</b> {s.kind} {s.date} — {s.place || "?"} {s.address_of_stay ? `— stayed at ${s.address_of_stay}` : ""}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setForm(p)} data-testid={`wallet-passport-edit-${p.id}`} className="text-xs px-2 py-1 border border-zinc-300">Edit</button>
                  <button onClick={() => del(p.id)} data-testid={`wallet-passport-delete-${p.id}`} className="p-1.5 border border-zinc-300 text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <div className="mt-4 border-2 border-sky-400 bg-sky-50/40 p-4 space-y-3" data-testid="wallet-passport-form">
          <div className="text-xs font-mono uppercase text-sky-700 mb-1">
            Editing {kindLabel(form.document_kind || "passport")}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Type">
              <select value={form.document_kind || "passport"} onChange={(e) => setForm({ ...form, document_kind: e.target.value })}
                data-testid="wallet-id-kind" className="mt-1 w-full border border-zinc-300 px-3 py-2">
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's License</option>
                <option value="national_id">National ID</option>
              </select>
            </Field>
            <Field label="Number *"><TextIn value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="wallet-passport-number" /></Field>
            <Field label="Country / State of issue *"><TextIn value={form.country_of_issue} onChange={(e) => setForm({ ...form, country_of_issue: e.target.value })} data-testid="wallet-passport-country" /></Field>
            <Field label="Place of issue"><TextIn value={form.place_of_issue || ""} onChange={(e) => setForm({ ...form, place_of_issue: e.target.value })} /></Field>
            <Field label="Holder name"><TextIn value={form.holder_name || ""} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} /></Field>
            <Field label="Date of issue"><TextIn type="date" value={form.date_of_issue || ""} onChange={(e) => setForm({ ...form, date_of_issue: e.target.value })} /></Field>
            <Field label="Date of expiry"><TextIn type="date" value={form.date_of_expiry || ""} onChange={(e) => setForm({ ...form, date_of_expiry: e.target.value })} /></Field>
          </div>
          {(form.document_kind === "drivers_license") && (
            <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-sky-200">
              <Field label="License class"><TextIn value={form.license_class || ""} onChange={(e) => setForm({ ...form, license_class: e.target.value })} placeholder="e.g. C" data-testid="wallet-license-class" /></Field>
              <Field label="Endorsements"><TextIn value={form.endorsements || ""} onChange={(e) => setForm({ ...form, endorsements: e.target.value })} placeholder="e.g. M, H" /></Field>
              <Field label="Restrictions"><TextIn value={form.restrictions || ""} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} placeholder="e.g. Corrective lenses" /></Field>
            </div>
          )}
          <Field label="Notes (previous visits, addresses of stay, visa numbers…)">
            <Area value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="wallet-passport-notes" />
          </Field>
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_current} onChange={(e) => setForm({ ...form, is_current: e.target.checked })} data-testid="wallet-passport-current" /> Current document
          </label>
          <div className="flex gap-2">
            <button onClick={save} data-testid="wallet-passport-save" className="px-4 py-2 bg-sky-600 text-white text-sm font-700">Save</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          </div>
          <p className="text-xs text-muted-foreground">ID-page or stamp photos live in the Documents Locker; the Vault stores the metadata.</p>
        </div>
      )}
    </section>
  );
}

/* ------------------------------ Medications ------------------------------- */

function Medications() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => refresh(setItems, "/wallet/medications");
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Medicine name is required"); return; }
    const method = form.id ? "patch" : "post";
    const url = form.id ? `/wallet/medications/${form.id}` : "/wallet/medications";
    const { data } = await api[method](url, form);
    setForm(data.id ? data : null);
    load();
    toast.success("Medication saved");
  };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/wallet/medications/${id}`); load(); } };

  return (
    <section data-testid="wallet-medications">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-800 text-xl">Your medication records</h2>
        <button onClick={() => setForm({ name: "", active: true, file_keys: [] })}
          data-testid="wallet-med-add" className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-base font-700">
          <Plus className="w-4 h-4" /> Add medicine
        </button>
      </div>
      {items.length === 0 && !form && (
        <div className="border border-dashed border-emerald-300 bg-emerald-50/40 p-4 text-sm">
          <p className="mb-1"><strong>Tip for travellers &amp; caregivers:</strong> take one clear photo of each pill bottle showing the <em>patient's name</em> and the <em>medicine name</em>. Customs officers and pharmacies love it.</p>
          <p className="text-xs text-emerald-800">Photos live inside your PIN-locked vault; nobody else can see them.</p>
        </div>
      )}
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="border border-zinc-200 bg-white p-3" data-testid={`wallet-med-${m.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-700 text-base">{m.name} {m.strength && <span className="text-sm text-zinc-500">· {m.strength}</span>}</div>
                {m.dosage && <div className="text-sm text-zinc-700">{m.dosage}</div>}
                {m.purpose && <div className="text-xs text-muted-foreground">For: {m.purpose}</div>}
                {m.prescribed_by && <div className="text-xs text-muted-foreground">Prescribed by {m.prescribed_by}</div>}
                {m.notes && <p className="text-xs text-zinc-600 mt-1">{m.notes}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setForm(m)} className="text-sm px-3 py-1.5 border border-zinc-300" data-testid={`wallet-med-edit-${m.id}`}>Edit</button>
                <button onClick={() => del(m.id)} className="p-2 border border-zinc-300 text-rose-600" data-testid={`wallet-med-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <MedPhotoStrip med={m} onChanged={load} />
          </div>
        ))}
      </div>

      {form && (
        <div className="mt-4 border-2 border-emerald-400 bg-emerald-50/40 p-4 space-y-3" data-testid="wallet-med-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Medicine name *"><TextIn value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wallet-med-name" /></Field>
            <Field label="Strength"><TextIn value={form.strength || ""} onChange={(e) => setForm({ ...form, strength: e.target.value })} placeholder="e.g. 10 mg" /></Field>
            <Field label="Prescribed dosage"><TextIn value={form.dosage || ""} onChange={(e) => setForm({ ...form, dosage: e.target.value })} placeholder="e.g. 1 tablet twice a day after food" /></Field>
            <Field label="Purpose"><TextIn value={form.purpose || ""} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Blood pressure" /></Field>
            <Field label="Prescribed by"><TextIn value={form.prescribed_by || ""} onChange={(e) => setForm({ ...form, prescribed_by: e.target.value })} /></Field>
            <Field label="Pharmacy"><TextIn value={form.pharmacy || ""} onChange={(e) => setForm({ ...form, pharmacy: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Area value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          {form.id && <MedPhotoUploader med={form} onChanged={() => { load(); }} />}
          <div className="flex gap-2">
            <button onClick={save} data-testid="wallet-med-save" className="px-5 py-2.5 bg-emerald-600 text-white text-base font-700">Save</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 border border-zinc-300 text-sm">Close</button>
          </div>
          {!form.id && <p className="text-xs text-emerald-800">Tip: save first, then add a photo of the bottle showing patient + medicine name.</p>}
        </div>
      )}
    </section>
  );
}

/* --------------- Medication prescription photos --------------- */

function MedPhotoUploader({ med, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo must be under 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", caption || "Prescription bottle (patient + medicine name visible)");
      // Let axios set the multipart boundary automatically; passing a manual
      // Content-Type here can strip the boundary and break the upload.
      await api.post(`/wallet/medications/${med.id}/photos`, fd);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
      toast.success("Prescription photo saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-emerald-200 bg-white p-3 space-y-2" data-testid="wallet-med-photo-uploader">
      <div className="flex items-center gap-2">
        <Camera className="w-5 h-5 text-emerald-700" />
        <div className="text-sm font-700">Add a photo of the pill bottle</div>
      </div>
      <p className="text-xs text-emerald-900">Make sure both the <strong>patient's name</strong> and the <strong>medicine name</strong> are clearly visible in the photo.</p>
      <TextIn placeholder="Caption (optional, e.g. 'Front of bottle')" value={caption}
        onChange={(e) => setCaption(e.target.value)} data-testid="wallet-med-photo-caption" />
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={pick}
          data-testid="wallet-med-photo-file" className="hidden" id={`med-photo-file-${med.id}`} />
        <label htmlFor={`med-photo-file-${med.id}`}
          data-testid="wallet-med-photo-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-700 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          Take / choose photo
        </label>
      </div>
    </div>
  );
}

function MedPhotoStrip({ med, onChanged }) {
  const photos = (med.file_keys || []).filter((p) => typeof p === "object" && p.key);
  if (photos.length === 0) return null;
  const del = async (key) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/wallet/medications/${med.id}/photos`, { params: { key } });
    onChanged?.();
  };
  const photoUrl = (key) => {
    const filename = key.split("/").pop(); // uuid.ext
    return `${api.defaults.baseURL}/wallet/medications/${med.id}/photos/${filename}`;
  };
  return (
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`wallet-med-photos-${med.id}`}>
      {photos.map((p) => (
        <div key={p.key} className="relative group border border-zinc-200 bg-zinc-50 aspect-square overflow-hidden">
          <img src={photoUrl(p.key)} alt={p.caption || "Prescription bottle"}
            className="w-full h-full object-cover" />
          {p.caption && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">{p.caption}</div>}
          <button onClick={() => del(p.key)} data-testid={`wallet-med-photo-del`}
            className="absolute top-1 right-1 bg-rose-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* -------------------------- Daily med schedule --------------------------- */

function Schedule() {
  const [meds, setMeds] = useState([]);
  const [sched, setSched] = useState([]);
  const [doses, setDoses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ medication_id: "", time_of_day: "morning", dose_amount: "1 tablet" });
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [m, s, d] = await Promise.all([
      api.get("/wallet/medications"),
      api.get("/wallet/med-schedule"),
      api.get("/wallet/med-doses", { params: { day: today } }),
    ]);
    setMeds(m.data.items || []); setSched(s.data.items || []); setDoses(d.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doseFor = (sid) => doses.find((x) => x.schedule_id === sid);
  const tick = async (sid, taken) => {
    setBusy(true);
    await api.post("/wallet/med-doses", { schedule_id: sid, taken, day: today });
    setBusy(false); load();
  };

  const addSchedule = async () => {
    if (!form.medication_id) { toast.error("Pick a medicine"); return; }
    await api.post("/wallet/med-schedule", { ...form, active: true, frequency: "daily" });
    setShowAdd(false); setForm({ medication_id: "", time_of_day: "morning", dose_amount: "1 tablet" });
    load();
  };
  const delSchedule = async (sid) => { if (window.confirm("Remove from schedule?")) { await api.delete(`/wallet/med-schedule/${sid}`); load(); } };

  return (
    <section data-testid="wallet-schedule">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-800 text-xl">Today's doses ({today})</h2>
        <button onClick={() => setShowAdd(true)} data-testid="wallet-sched-add" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-sm font-700">
          <Plus className="w-4 h-4" /> Add to schedule
        </button>
      </div>
      {sched.length === 0 && <p className="text-sm text-muted-foreground">No medicines on today's schedule yet.</p>}
      <div className="space-y-2">
        {sched.map((s) => {
          const med = meds.find((m) => m.id === s.medication_id);
          const dose = doseFor(s.id);
          return (
            <div key={s.id} className="border border-zinc-200 bg-white p-3 flex items-center justify-between" data-testid={`wallet-sched-${s.id}`}>
              <div>
                <div className="font-700">{med?.name || "(deleted medicine)"} — <span className="text-sm text-zinc-500">{s.time_of_day}</span></div>
                <div className="text-xs text-muted-foreground">{s.dose_amount}{med?.strength ? ` · ${med.strength}` : ""}</div>
                {dose?.notes && <p className="text-xs italic mt-0.5">{dose.notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button disabled={busy} onClick={() => tick(s.id, !(dose?.taken))} data-testid={`wallet-sched-tick-${s.id}`}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-700 ${dose?.taken ? "bg-emerald-600 text-white" : "border border-zinc-400 text-zinc-700"}`}>
                  {dose?.taken ? <><Check className="w-4 h-4" /> Taken</> : busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark taken"}
                </button>
                <button onClick={() => delSchedule(s.id)} className="p-1.5 border border-zinc-300 text-rose-600" data-testid={`wallet-sched-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
      {showAdd && (
        <div className="mt-4 border-2 border-violet-400 bg-violet-50/40 p-4 space-y-3" data-testid="wallet-sched-form">
          <Field label="Medicine">
            <select value={form.medication_id} onChange={(e) => setForm({ ...form, medication_id: e.target.value })}
              data-testid="wallet-sched-med" className="mt-1 w-full border border-zinc-300 px-3 py-2">
              <option value="">— pick medicine —</option>
              {meds.map((m) => <option key={m.id} value={m.id}>{m.name}{m.strength ? ` (${m.strength})` : ""}</option>)}
            </select>
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Time of day"><TextIn value={form.time_of_day} onChange={(e) => setForm({ ...form, time_of_day: e.target.value })} /></Field>
            <Field label="Dose amount"><TextIn value={form.dose_amount} onChange={(e) => setForm({ ...form, dose_amount: e.target.value })} /></Field>
          </div>
          <div className="flex gap-2">
            <button onClick={addSchedule} data-testid="wallet-sched-save" className="px-4 py-2 bg-violet-600 text-white text-sm font-700">Add</button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          </div>
          <p className="text-xs text-muted-foreground">Crossing time zones? Confirm timing changes with your pharmacist before adjusting.</p>
        </div>
      )}
    </section>
  );
}

/* -------------------------------- Receipts ------------------------------- */

function Receipts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { refresh(setItems, "/wallet/receipts"); }, []);

  const save = async () => {
    if (!form.amount) { toast.error("Amount is required"); return; }
    await api.post("/wallet/receipts", { ...form, amount: Number(form.amount) });
    setForm(null); refresh(setItems, "/wallet/receipts");
    toast.success("Receipt saved");
  };
  const link = async (id) => {
    setBusy(true);
    const { data } = await api.post(`/wallet/receipts/${id}/link-expense`);
    setBusy(false);
    toast.success(data.already ? "Already linked to your expense tracker" : "Added to your expense tracker");
    refresh(setItems, "/wallet/receipts");
  };
  const del = async (id) => { if (window.confirm("Delete? This also removes any linked expense.")) { await api.delete(`/wallet/receipts/${id}`); refresh(setItems, "/wallet/receipts"); } };

  return (
    <section data-testid="wallet-receipts">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-800 text-xl">Trip receipts</h2>
        <button onClick={() => setForm({ amount: "", currency: "USD", purchased_on: new Date().toISOString().slice(0, 10) })}
          data-testid="wallet-receipt-add" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-sm font-700">
          <Plus className="w-4 h-4" /> Add receipt
        </button>
      </div>
      {items.length === 0 && !form && <p className="text-sm text-muted-foreground">No receipts yet. Add purchases here, then send each to your main expense tracker.</p>}
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="border border-zinc-200 bg-white p-3 flex items-start justify-between" data-testid={`wallet-receipt-${r.id}`}>
            <div>
              <div className="font-700">{r.merchant || "(no merchant)"} — <span className="text-amber-800">{r.currency} {Number(r.amount).toFixed(2)}</span></div>
              <div className="text-xs text-muted-foreground">{r.purchased_on || "—"} · {r.category || "Travel"}{r.linked_expense_id && " · linked to expense tracker"}</div>
              {r.notes && <p className="text-xs italic mt-0.5">{r.notes}</p>}
            </div>
            <div className="flex flex-col gap-1 items-end">
              {!r.linked_expense_id ? (
                <button disabled={busy} onClick={() => link(r.id)} data-testid={`wallet-receipt-link-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-amber-500 text-amber-700 font-700">
                  <Link2 className="w-3 h-3" /> Add to expenses
                </button>
              ) : (
                <Link to="/expenses" target="_blank" data-testid={`wallet-receipt-open-${r.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-emerald-700">
                  <ExternalLink className="w-3 h-3" /> Open expense
                </Link>
              )}
              <button onClick={() => del(r.id)} className="p-1.5 border border-zinc-300 text-rose-600" data-testid={`wallet-receipt-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {form && (
        <div className="mt-4 border-2 border-amber-400 bg-amber-50/40 p-4 space-y-3" data-testid="wallet-receipt-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Merchant"><TextIn value={form.merchant || ""} onChange={(e) => setForm({ ...form, merchant: e.target.value })} data-testid="wallet-receipt-merchant" /></Field>
            <Field label="Amount *"><TextIn type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="wallet-receipt-amount" /></Field>
            <Field label="Currency"><TextIn value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} data-testid="wallet-receipt-currency" /></Field>
            <Field label="Purchased on"><TextIn type="date" value={form.purchased_on || ""} onChange={(e) => setForm({ ...form, purchased_on: e.target.value })} /></Field>
            <Field label="Category"><TextIn value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Meals, Shopping…" /></Field>
            <Field label="Trip id (optional)"><TextIn value={form.trip_id || ""} onChange={(e) => setForm({ ...form, trip_id: e.target.value })} placeholder="paste trip id" /></Field>
          </div>
          <Field label="Notes"><Area value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex gap-2">
            <button onClick={save} data-testid="wallet-receipt-save" className="px-4 py-2 bg-amber-600 text-white text-sm font-700">Save</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          </div>
          <p className="text-xs text-muted-foreground">Receipt photos go in the Documents Locker; the Wallet stores the amount/date, and "Add to expenses" pushes it to your tracker exactly once.</p>
        </div>
      )}
    </section>
  );
}

/* ---------------- Vault tools: emergency contacts + PDF export ---------------- */

function VaultTools() {
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", relationship: "" });
  const [expPin, setExpPin] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/wallet/vault/trusted-contacts"),
        api.get("/wallet/vault/emergency/requests"),
      ]);
      setContacts(c.data.contacts || []); setRequests(r.data.requests || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const addContact = async () => {
    if (!form.email) return toast.error("Email required");
    try {
      await api.post("/wallet/vault/trusted-contacts", form);
      setForm({ email: "", name: "", relationship: "" });
      load(); toast.success("Trusted contact added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const rmContact = async (id) => {
    if (!window.confirm("Remove this trusted contact?")) return;
    await api.delete(`/wallet/vault/trusted-contacts/${id}`);
    load();
  };
  const deny = async (id) => {
    await api.post(`/wallet/vault/emergency/requests/${id}/deny`);
    load(); toast.success("Request denied");
  };
  const grantNow = async (id) => {
    if (!window.confirm("Grant read-only access now, skipping the 12h wait?")) return;
    await api.post(`/wallet/vault/emergency/requests/${id}/grant`);
    load(); toast.success("Access granted immediately");
  };

  const exportPdf = async () => {
    if (expPin.length < 4) return toast.error("Enter your vault PIN to encrypt the export");
    setBusy(true);
    try {
      const res = await api.post("/wallet/vault/export/pdf", { pin: expPin }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `travelerpulse-vault-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Encrypted PDF ready — open it with your vault PIN");
      setExpPin("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 grid md:grid-cols-2 gap-4 border-t border-zinc-200 pt-6" data-testid="wallet-vault-tools">
      {/* Emergency contacts */}
      <section className="border border-rose-200 bg-rose-50/40 p-4" data-testid="wallet-emergency-section">
        <div className="flex items-center gap-2 mb-2">
          <LifeBuoy className="w-5 h-5 text-rose-700" />
          <h3 className="font-heading font-800 text-lg">Emergency trusted contact</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          A trusted family member can request read-only vault access. You get 12 hours to deny before access is released.
        </p>
        <div className="space-y-2 mb-3">
          {contacts.length === 0 && <p className="text-xs text-muted-foreground">No trusted contacts yet.</p>}
          {contacts.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-white border border-rose-200 p-2 text-sm" data-testid={`wallet-contact-${c.id}`}>
              <div>
                <div className="font-700">{c.name || c.email} <span className="text-xs text-zinc-500 font-normal">· {c.relationship || "trusted"}</span></div>
                <div className="text-xs text-muted-foreground">{c.email}</div>
              </div>
              <button onClick={() => rmContact(c.id)} className="p-1.5 border border-zinc-300 text-rose-600" data-testid={`wallet-contact-remove-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="wallet-contact-email" className="border border-zinc-300 px-2 py-1.5 text-sm" />
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-zinc-300 px-2 py-1.5 text-sm" />
          <input placeholder="Relationship" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}
            className="border border-zinc-300 px-2 py-1.5 text-sm" />
        </div>
        <button onClick={addContact} data-testid="wallet-contact-add" className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-rose-600 text-white text-sm font-700">
          <Plus className="w-4 h-4" /> Add trusted contact
        </button>

        {requests.length > 0 && (
          <div className="mt-4 border-t border-rose-200 pt-3">
            <h4 className="text-sm font-700 mb-2">Pending / recent access requests</h4>
            <ul className="space-y-1.5 text-xs">
              {requests.map((r) => (
                <li key={r.id} className="flex items-center justify-between bg-white border border-rose-200 p-2" data-testid={`wallet-req-${r.id}`}>
                  <div>
                    <div className="font-700">{r.contact_email}</div>
                    <div className="text-muted-foreground">
                      {r.status === "pending" && <><Clock className="w-3 h-3 inline -mt-0.5" /> releases at {new Date(r.release_at).toLocaleString()}</>}
                      {r.status === "denied" && <><XCircle className="w-3 h-3 inline -mt-0.5 text-rose-500" /> denied</>}
                      {r.status === "released" && "read-only access active"}
                      {r.status === "cancelled" && "cancelled"}
                    </div>
                    {r.reason && <div className="italic text-muted-foreground truncate max-w-[240px]" title={r.reason}>“{r.reason}”</div>}
                  </div>
                  {r.status === "pending" && (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => grantNow(r.id)} data-testid={`wallet-req-grant-${r.id}`}
                        className="text-xs px-2 py-1 border border-emerald-500 text-emerald-700 font-700">Grant now</button>
                      <button onClick={() => deny(r.id)} data-testid={`wallet-req-deny-${r.id}`}
                        className="text-xs px-2 py-1 bg-rose-600 text-white font-700">Deny</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* PDF export */}
      <section className="border border-sky-200 bg-sky-50/40 p-4" data-testid="wallet-export-section">
        <div className="flex items-center gap-2 mb-2">
          <Download className="w-5 h-5 text-sky-700" />
          <h3 className="font-heading font-800 text-lg">Encrypted PDF export</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Download a PIN-encrypted PDF of your vault (passports, licenses, medications). Each page is watermarked with your email + export time so any leak is traceable.
        </p>
        <label className="block text-sm mb-2"><span className="font-600">Confirm your vault PIN</span>
          <input type="password" inputMode="numeric" maxLength={12} value={expPin}
            onChange={(e) => setExpPin(e.target.value.replace(/\D/g, ""))}
            data-testid="wallet-export-pin"
            className="mt-1 w-full border border-zinc-300 px-3 py-2 font-mono tracking-widest" />
        </label>
        <button onClick={exportPdf} disabled={busy} data-testid="wallet-export-download"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-sky-600 text-white font-700 text-sm disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download encrypted PDF
        </button>
        <p className="mt-2 text-[11px] text-muted-foreground">AES-128, PIN-locked. Watermark stamps every page with your email + timestamp.</p>
      </section>
    </div>
  );
}

/* ---------------- Notification preferences ---------------- */

const NOTIF_META = [
  { key: "emergency_request", label: "Emergency access request", hint: "A trusted contact opened a request" },
  { key: "emergency_granted", label: "You granted emergency access", hint: "Confirmation email for peace of mind" },
  { key: "emergency_denied",  label: "You denied a request", hint: "Confirmation email when you deny" },
  { key: "failed_unlocks",    label: "Suspicious unlock attempts", hint: "3+ failed PIN/biometric tries in 10 minutes" },
  { key: "vault_export",      label: "PDF export downloaded", hint: "Someone just downloaded your vault PDF" },
  { key: "quiet_auto_denied", label: "Quiet-hours auto-deny", hint: "A request was blocked during your quiet hours" },
];

function VaultNotificationPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [smsAvailable, setSmsAvailable] = useState(false);
  const [smsNote, setSmsNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wallet/vault/notification-prefs");
      setPrefs(data.prefs);
      setSmsAvailable(!!data.sms_available);
      setSmsNote(data.sms_note || "");
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const toggle = (key, ch) => setPrefs((p) => ({ ...p, [key]: { ...p[key], [ch]: !p[key][ch] } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/wallet/vault/notification-prefs", { prefs });
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!prefs) return null;

  return (
    <section className="mt-6 border border-indigo-200 bg-indigo-50/40 p-4" data-testid="wallet-notif-prefs">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-5 h-5 text-indigo-700" />
        <h3 className="font-heading font-800 text-lg">Owner notifications</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Choose which events you want to be emailed about. {smsNote}
      </p>
      <div className="space-y-2">
        {NOTIF_META.map((m) => (
          <div key={m.key} className="flex items-center justify-between gap-2 bg-white border border-indigo-200 p-2.5" data-testid={`wallet-notif-${m.key}`}>
            <div className="min-w-0">
              <div className="font-700 text-sm">{m.label}</div>
              <div className="text-xs text-muted-foreground truncate">{m.hint}</div>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!prefs[m.key]?.email}
                  onChange={() => toggle(m.key, "email")}
                  data-testid={`wallet-notif-email-${m.key}`}
                  className="w-4 h-4 accent-indigo-600" />
                Email
              </label>
              <label className={`text-xs inline-flex items-center gap-1.5 ${smsAvailable ? "cursor-pointer" : "opacity-50"}`} title={smsAvailable ? "SMS" : "SMS via Firebase — coming soon"}>
                <input type="checkbox" checked={!!prefs[m.key]?.sms}
                  disabled={!smsAvailable}
                  onChange={() => toggle(m.key, "sms")}
                  data-testid={`wallet-notif-sms-${m.key}`}
                  className="w-4 h-4 accent-indigo-600" />
                SMS {!smsAvailable && <span className="text-[10px] text-zinc-500">(soon)</span>}
              </label>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="wallet-notif-save"
        className="mt-3 inline-flex items-center gap-1.5 px-5 py-2.5 bg-indigo-600 text-white text-sm font-700 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Save preferences
      </button>
    </section>
  );
}

/* ---------------- Quiet hours (auto-deny window) ---------------- */

function VaultQuietHours() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wallet/vault/quiet-hours");
      const tz = -new Date().getTimezoneOffset(); // minutes east of UTC
      setState({
        enabled: !!data.enabled,
        start_hour: data.start_hour ?? "",
        end_hour: data.end_hour ?? "",
        tz_offset_minutes: data.tz_offset_minutes ?? tz,
        is_first_setup: !!data.is_first_setup,
      });
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (state.enabled) {
      if (state.start_hour === "" || state.end_hour === "") { toast.error("Set a start and end hour"); return; }
      if (Number(state.start_hour) === Number(state.end_hour)) { toast.error("Start and end must differ"); return; }
    }
    setBusy(true);
    try {
      await api.put("/wallet/vault/quiet-hours", {
        enabled: state.enabled,
        start_hour: Number(state.start_hour || 0),
        end_hour: Number(state.end_hour || 0),
        tz_offset_minutes: Number(state.tz_offset_minutes || 0),
      });
      toast.success(state.enabled ? "Quiet hours active" : "Quiet hours turned off");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  const hourOpts = Array.from({ length: 24 }, (_, i) => i);
  const fmt = (h) => `${String(h).padStart(2, "0")}:00`;

  return (
    <section className="mt-6 border border-violet-200 bg-violet-50/40 p-4" data-testid="wallet-quiet-hours">
      <div className="flex items-center gap-2 mb-2">
        <Moon className="w-5 h-5 text-violet-700" />
        <h3 className="font-heading font-800 text-lg">Quiet hours (auto-deny window)</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        When enabled, new emergency access requests during this window will be <strong>auto-denied</strong> and the contact is asked to try again during your active hours. Handy for overnight peace of mind.
      </p>
      {state.is_first_setup && (
        <div className="text-xs bg-white border border-violet-300 text-violet-900 p-2 mb-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>No default set. Pick times that work for you (e.g., 22:00 → 07:00 is common).</span>
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        <label className="inline-flex items-center gap-2 text-sm font-700 cursor-pointer">
          <input type="checkbox" checked={state.enabled}
            onChange={(e) => setState({ ...state, enabled: e.target.checked })}
            data-testid="wallet-quiet-enabled"
            className="w-5 h-5 accent-violet-600" />
          Enable quiet hours
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Field label="Start (your local time)">
          <select value={state.start_hour} onChange={(e) => setState({ ...state, start_hour: e.target.value })}
            disabled={!state.enabled} data-testid="wallet-quiet-start"
            className="mt-1 w-full border border-zinc-300 px-3 py-2 disabled:opacity-50">
            <option value="">— pick —</option>
            {hourOpts.map((h) => <option key={h} value={h}>{fmt(h)}</option>)}
          </select>
        </Field>
        <Field label="End (your local time)">
          <select value={state.end_hour} onChange={(e) => setState({ ...state, end_hour: e.target.value })}
            disabled={!state.enabled} data-testid="wallet-quiet-end"
            className="mt-1 w-full border border-zinc-300 px-3 py-2 disabled:opacity-50">
            <option value="">— pick —</option>
            {hourOpts.map((h) => <option key={h} value={h}>{fmt(h)}</option>)}
          </select>
        </Field>
      </div>
      <button onClick={save} disabled={busy} data-testid="wallet-quiet-save"
        className="mt-3 inline-flex items-center gap-1.5 px-5 py-2.5 bg-violet-600 text-white text-sm font-700 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Save quiet hours
      </button>
      <p className="mt-2 text-[11px] text-muted-foreground">Times are stored in your local timezone. Contacts see a message asking them to try again after your window ends.</p>
    </section>
  );
}


/* ---------------- Vault activity log ---------------- */

const EVENT_LABEL = {
  vault_unlocked: { label: "Vault unlocked", tone: "text-emerald-700" },
  vault_unlock_failed: { label: "Failed PIN attempt", tone: "text-rose-700" },
  vault_pdf_exported: { label: "PDF exported", tone: "text-sky-700" },
  emergency_request_opened: { label: "Emergency request opened", tone: "text-amber-700" },
  emergency_request_granted: { label: "Emergency access granted", tone: "text-amber-800" },
  emergency_request_denied: { label: "Emergency access denied", tone: "text-zinc-700" },
  emergency_request_auto_denied: { label: "Quiet-hours auto-deny", tone: "text-violet-700" },
  suspicious_unlock_alert: { label: "Suspicious unlock alert sent", tone: "text-rose-800" },
};

function VaultActivity() {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ from: "", to: "" });
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/wallet/vault/activity"); setRows(data.activity || []); }
    catch { /* silent */ }
  })(); }, []);

  const lastUnlock = rows.find((r) => r.event === "vault_unlocked");
  const lastExport = rows.find((r) => r.event === "vault_pdf_exported");

  const exportCsv = async () => {
    try {
      const params = {};
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      const res = await api.get("/wallet/vault/activity.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `travelerpulse-vault-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Activity log downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  if (rows.length === 0) return null;

  return (
    <section className="mt-6 border border-zinc-200 bg-white p-4" data-testid="wallet-activity-section">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-zinc-700" />
          <h3 className="font-heading font-800 text-lg">Vault activity</h3>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="wallet-activity-csv-controls">
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
            data-testid="wallet-activity-from" aria-label="From date"
            className="border border-zinc-300 px-2 py-1.5 text-sm" />
          <span className="text-xs text-zinc-500">to</span>
          <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
            data-testid="wallet-activity-to" aria-label="To date"
            className="border border-zinc-300 px-2 py-1.5 text-sm" />
          <button onClick={exportCsv} data-testid="wallet-activity-csv"
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-400 text-sm font-700 hover:bg-zinc-50">
            <FileDown className="w-4 h-4" /> Download CSV
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <div className="text-xs bg-emerald-50 border border-emerald-200 p-2">
          <div className="text-emerald-700 font-mono uppercase tracking-wide">Last opened</div>
          <div className="font-700 text-zinc-900">{lastUnlock ? new Date(lastUnlock.created_at).toLocaleString() : "—"}</div>
          <div className="text-zinc-600">{lastUnlock?.detail || "No unlock recorded yet"}</div>
        </div>
        <div className="text-xs bg-sky-50 border border-sky-200 p-2">
          <div className="text-sky-700 font-mono uppercase tracking-wide">Last exported</div>
          <div className="font-700 text-zinc-900">{lastExport ? new Date(lastExport.created_at).toLocaleString() : "—"}</div>
          <div className="text-zinc-600">{lastExport?.detail || "No export yet"}</div>
        </div>
      </div>
      <details className="text-xs" data-testid="wallet-activity-timeline">
        <summary className="cursor-pointer text-zinc-600 font-700">Show full activity ({rows.length})</summary>
        <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto pr-1">
          {rows.map((r, i) => {
            const meta = EVENT_LABEL[r.event] || { label: r.event, tone: "text-zinc-700" };
            return (
              <li key={r.id || i} className="flex items-start gap-2 border-b border-zinc-100 pb-1" data-testid={`wallet-activity-${r.event}`}>
                <ShieldCheck className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.tone}`} />
                <div>
                  <div className={`font-700 ${meta.tone}`}>{meta.label}</div>
                  <div className="text-zinc-600">{r.detail}</div>
                  <div className="text-[10px] text-zinc-400 font-mono">{new Date(r.created_at).toLocaleString()}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </details>
      <p className="mt-2 text-[11px] text-muted-foreground">If you don't recognise a recent unlock or export, change your Vault PIN immediately.</p>
    </section>
  );
}


/* ================================================================
   My Bags — Packing Buddy
   ================================================================ */

const BAG_TYPES = [
  { value: "large", label: "Large check-in" },
  { value: "roll_on", label: "Roll-on / carry-on" },
  { value: "backpack", label: "Backpack" },
  { value: "other", label: "Other" },
];

function Bags() {
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState(null);
  const [manifestPickerOpen, setManifestPickerOpen] = useState(false);
  const load = () => refresh(setItems, "/wallet/bags");
  useEffect(() => { load(); api.get("/wallet/bags/trips-lite").then((r) => setTrips(r.data.items || [])).catch(() => {}); }, []);

  const emptyBag = () => ({
    owner_name: "", bag_count: 1, bag_type: "large", bag_type_other: "",
    travel_date: "", airline: "", flight_number: "", from_airport: "", to_airport: "",
    tags: [{ number: "", image_key: null }], notes: "", packing_checklist_id: null, photos: [],
    trip_id: null, shared_with: [], public_photo_enabled: false,
  });

  const downloadLostSheet = async (bag) => {
    try {
      const res = await api.get(`/wallet/bags/${bag.id}/lost-sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `lost-bag-${(bag.owner_name || "bag").toLowerCase().replace(/\s/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Lost-bag sheet downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build PDF"); }
  };

  const downloadTravellerCard = async (bag) => {
    try {
      const res = await api.get(`/wallet/bags/${bag.id}/traveller-card.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `traveller-card-${(bag.owner_name || "bag").toLowerCase().replace(/\s/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Traveller card ready — print & tuck inside the bag");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build card"); }
  };

  const downloadManifest = async (tripId) => {
    setManifestPickerOpen(false);
    try {
      const res = await api.get("/wallet/bags/manifest.pdf", { params: { trip_id: tripId }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `baggage-manifest-${tripId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Manifest downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "No bags linked to this trip yet"); }
  };

  const shareBag = async (bag) => {
    // Family Space invite reuse — offer the current user's family members as
    // one-tap picks. Falls back to a typed email when the user has no family.
    let family = [];
    try { const r = await api.get("/wallet/bags/family-emails"); family = r.data.items || []; } catch { /* empty */ }
    const alreadyShared = new Set((bag.shared_with || []).map((e) => e.toLowerCase()));
    const options = family.filter((m) => m.email && !alreadyShared.has(m.email.toLowerCase()));
    let email = null;
    if (options.length > 0) {
      const picks = options.map((m, i) => `${i + 1}. ${m.name || m.email} (${m.email})${m.status === "invited" ? " · invited" : ""}`).join("\n");
      const choice = window.prompt(`Share "${bag.owner_name}" bag with a family member:\n\n${picks}\n\nType a number, or paste an email:`);
      if (!choice) return;
      const n = parseInt(choice, 10);
      email = (!isNaN(n) && n >= 1 && n <= options.length) ? options[n - 1].email : choice.trim();
    } else {
      email = window.prompt("Family member's email (read-only access):");
    }
    if (!email) return;
    try {
      const { data } = await api.post(`/wallet/bags/${bag.id}/share`, { email });
      load();
      toast.success(`Shared with ${email}`);
      if (data.share_token) toast.info("Public return-URL enabled — appears as a QR on the PDF.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not share"); }
  };
  const unshareBag = async (bag, email) => {
    if (!window.confirm(`Remove ${email} from this bag?`)) return;
    await api.post(`/wallet/bags/${bag.id}/unshare`, { email });
    load();
  };

  const save = async () => {
    if (!form.owner_name.trim()) { toast.error("Owner name is required"); return; }
    try {
      const method = form.id ? "patch" : "post";
      const url = form.id ? `/wallet/bags/${form.id}` : "/wallet/bags";
      const { data } = await api[method](url, {
        ...form, bag_count: Number(form.bag_count) || 1,
        tags: (form.tags || []).filter((t) => t.number.trim() || t.image_key),
      });
      setForm(data.id ? data : null);
      load();
      toast.success("Bag saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const del = async (id) => { if (window.confirm("Delete this bag record?")) { await api.delete(`/wallet/bags/${id}`); load(); } };

  return (
    <section data-testid="wallet-bags">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-800 text-xl flex items-center gap-2"><Luggage className="w-5 h-5 text-indigo-600" /> My Bags</h2>
          <p className="text-xs text-muted-foreground">Track every bag on a trip. Tags, photos, owner and flight — all in one place.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {trips.length > 0 && (
            <div className="relative">
              <button onClick={() => setManifestPickerOpen((o) => !o)} data-testid="wallet-bags-manifest-picker"
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-primary text-primary text-sm font-700 hover:bg-primary hover:text-primary-foreground transition-colors rounded">
                <FileDown className="w-4 h-4" /> Trip manifest
              </button>
              {manifestPickerOpen && (
                <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-warm-lg min-w-[240px] z-30 py-1" data-testid="wallet-bags-manifest-menu">
                  {trips.map((t) => (
                    <button key={t.id} onClick={() => downloadManifest(t.id)}
                      data-testid={`wallet-bags-manifest-${t.id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 truncate">
                      <div className="font-700 text-primary">{t.title}</div>
                      {t.start_date && <div className="text-[11px] text-muted-foreground">{t.start_date}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <Link to="/packing" data-testid="wallet-bags-packing-buddy"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-indigo-300 text-indigo-700 text-sm font-700 hover:bg-indigo-50">
            <ClipboardCheckIcon /> Packing Buddy
          </Link>
          <button onClick={() => setForm(emptyBag())} data-testid="wallet-bag-add"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-base font-700 hover:bg-indigo-700">
            <Plus className="w-4 h-4" /> Add bag record
          </button>
        </div>
      </div>

      {items.length === 0 && !form && (
        <div className="border border-dashed border-indigo-300 bg-indigo-50/40 p-4 text-sm">
          <p><strong>Tip:</strong> add a record for each trip. Snap a photo of every bag &amp; its tag — makes lost-baggage claims fast at the airport.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((b) => (
          <div key={b.id} className="border border-zinc-200 bg-white p-3" data-testid={`wallet-bag-${b.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-700 text-base flex items-center gap-2">
                  <Luggage className="w-4 h-4 text-indigo-600" />
                  {b.owner_name} · {b.bag_count} bag{b.bag_count > 1 ? "s" : ""} <span className="text-xs font-500 text-muted-foreground">({b.bag_type_other || (BAG_TYPES.find((t) => t.value === b.bag_type)?.label)})</span>
                </div>
                {(b.airline || b.flight_number || b.from_airport || b.to_airport) && (
                  <div className="text-sm text-zinc-700 flex items-center gap-1 flex-wrap mt-0.5">
                    <Plane className="w-3.5 h-3.5 -rotate-45 text-zinc-500" />
                    {b.airline}{b.flight_number && ` ${b.flight_number}`}
                    {b.from_airport && ` · ${b.from_airport}`}{b.to_airport && ` → ${b.to_airport}`}
                    {b.travel_date && <span className="text-xs text-muted-foreground">· {b.travel_date}</span>}
                  </div>
                )}
                {(b.tags || []).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                    <TagIcon className="w-3 h-3" />
                    Tags: {(b.tags || []).map((t) => t.number).filter(Boolean).join(", ") || "—"}
                  </div>
                )}
                {b.notes && <p className="text-xs text-zinc-600 mt-1 line-clamp-2">{b.notes}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                {!b._read_only && (
                  <>
                    <button onClick={() => downloadLostSheet(b)} className="text-sm px-2 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1" data-testid={`wallet-bag-pdf-${b.id}`} title="Lost-bag quick sheet PDF">
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button onClick={() => downloadTravellerCard(b)} className="text-sm px-2 py-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1" data-testid={`wallet-bag-card-${b.id}`} title="Printable wallet-size traveller card">
                      <TagIcon className="w-3.5 h-3.5" /> Card
                    </button>
                    <button onClick={() => shareBag(b)} className="text-sm px-2 py-1.5 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 inline-flex items-center gap-1" data-testid={`wallet-bag-share-${b.id}`} title="Share read-only with family">
                      <Users className="w-3.5 h-3.5" /> Share
                    </button>
                    <button onClick={() => setForm(b)} className="text-sm px-3 py-1.5 border border-zinc-300" data-testid={`wallet-bag-edit-${b.id}`}>Edit</button>
                    <button onClick={() => del(b.id)} className="p-2 border border-zinc-300 text-rose-600" data-testid={`wallet-bag-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
                {b._read_only && (
                  <button onClick={() => downloadLostSheet(b)} className="text-sm px-2 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1" data-testid={`wallet-bag-pdf-${b.id}`}>
                    <FileDown className="w-3.5 h-3.5" /> PDF
                  </button>
                )}
              </div>
            </div>
            {b._read_only && (
              <div className="mt-1 text-xs text-indigo-800 inline-flex items-center gap-1"><Users className="w-3 h-3" /> Shared with you by <strong>{b._shared_from_name}</strong> · read-only</div>
            )}
            {!b._read_only && (b.shared_with || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1" data-testid={`wallet-bag-shared-list-${b.id}`}>
                {(b.shared_with || []).map((em) => (
                  <span key={em} className="text-[11px] bg-indigo-50 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    {em} <button onClick={() => unshareBag(b, em)} className="text-indigo-400 hover:text-rose-600">×</button>
                  </span>
                ))}
              </div>
            )}
            <BagPhotoStrip bag={b} onChanged={load} />
          </div>
        ))}
      </div>

      {form && <BagForm form={form} setForm={setForm} onSave={save} onCancel={() => setForm(null)} onChanged={load} trips={trips} />}
    </section>
  );
}

const ClipboardCheckIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" />
  </svg>
);

function BagForm({ form, setForm, onSave, onCancel, onChanged, trips = [] }) {
  const addTag = () => setForm({ ...form, tags: [...(form.tags || []), { number: "", image_key: null }] });
  const updateTag = (i, patch) => setForm({ ...form, tags: form.tags.map((t, idx) => idx === i ? { ...t, ...patch } : t) });
  const removeTag = (i) => setForm({ ...form, tags: form.tags.filter((_, idx) => idx !== i) });

  return (
    <div className="mt-4 border-2 border-indigo-400 bg-indigo-50/40 p-4 space-y-3" data-testid="wallet-bag-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Owner of the bag *">
          <TextIn value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="wallet-bag-owner" placeholder="e.g. Mom, Dad, Rohan" />
        </Field>
        <Field label="Number of bags *">
          <TextIn type="number" min="1" max="20" value={form.bag_count} onChange={(e) => setForm({ ...form, bag_count: e.target.value })} data-testid="wallet-bag-count" />
        </Field>
        <Field label="Type of bag">
          <select value={form.bag_type} onChange={(e) => setForm({ ...form, bag_type: e.target.value })}
            data-testid="wallet-bag-type"
            className="mt-1 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-primary">
            {BAG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        {form.bag_type === "other" && (
          <Field label="Describe">
            <TextIn value={form.bag_type_other || ""} onChange={(e) => setForm({ ...form, bag_type_other: e.target.value })}
              data-testid="wallet-bag-type-other" placeholder="e.g. cabin trunk, cello case" />
          </Field>
        )}
        <Field label="Date of travel">
          <TextIn type="date" value={form.travel_date || ""} onChange={(e) => setForm({ ...form, travel_date: e.target.value })} data-testid="wallet-bag-date" />
        </Field>
        <Field label="Airline">
          <TextIn value={form.airline || ""} onChange={(e) => setForm({ ...form, airline: e.target.value })} data-testid="wallet-bag-airline" placeholder="e.g. Emirates" />
        </Field>
        <Field label="Flight number">
          <TextIn value={form.flight_number || ""} onChange={(e) => setForm({ ...form, flight_number: e.target.value })} data-testid="wallet-bag-flight" placeholder="e.g. EK 526" />
        </Field>
        <Field label="From (airport / city)">
          <TextIn value={form.from_airport || ""} onChange={(e) => setForm({ ...form, from_airport: e.target.value })} data-testid="wallet-bag-from" placeholder="COK / Kochi" />
        </Field>
        <Field label="To (airport / city)">
          <TextIn value={form.to_airport || ""} onChange={(e) => setForm({ ...form, to_airport: e.target.value })} data-testid="wallet-bag-to" placeholder="DXB / Dubai" />
        </Field>
        <Field label="Link to a trip (optional)">
          <select value={form.trip_id || ""} onChange={(e) => setForm({ ...form, trip_id: e.target.value || null })}
            data-testid="wallet-bag-trip"
            className="mt-1 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-primary">
            <option value="">— not linked —</option>
            {trips.map((t) => (
              <option key={t.id} value={t.id}>{t.title}{t.start_date ? ` · ${t.start_date}` : ""}</option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground mt-1">Bags linked to a trip appear on that trip's day-of-travel screen.</p>
        </Field>
      </div>

      {/* Bag tags — number + photo per tag */}
      <div className="bg-white border border-indigo-200 p-3" data-testid="wallet-bag-tags-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-700 flex items-center gap-1.5"><TagIcon className="w-4 h-4 text-indigo-600" /> Bag tags — number &amp; photo</div>
          <button onClick={addTag} data-testid="wallet-bag-tag-add" className="text-xs px-2 py-1 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add tag
          </button>
        </div>
        <div className="space-y-2">
          {(form.tags || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`wallet-bag-tag-${i}`}>
              <TextIn value={t.number} onChange={(e) => updateTag(i, { number: e.target.value })}
                data-testid={`wallet-bag-tag-num-${i}`} placeholder={`Tag #${i + 1} — e.g. EK 0083654`}
                className="flex-1 border border-zinc-300 px-3 py-2 outline-none focus:border-primary" />
              {form.id && (
                <BagTagPhotoBtn bag={form} tagIndex={i} tag={t} onChanged={onChanged} setForm={setForm} />
              )}
              <button onClick={() => removeTag(i)} className="p-2 border border-zinc-300 text-rose-600" data-testid={`wallet-bag-tag-del-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {form.tags.length === 0 && <p className="text-xs text-muted-foreground">No tags yet. Add one for each checked-in bag so a lost-baggage claim is instant.</p>}
        </div>
      </div>

      <Field label="Notes">
        <Area value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="wallet-bag-notes" placeholder="Anything else useful for this bag / trip" />
      </Field>

      {/* Packing buddy link */}
      <div className="bg-white border border-indigo-200 p-3 flex items-center justify-between gap-2" data-testid="wallet-bag-packing-link">
        <div className="text-sm">
          <div className="font-700">Packing Buddy checklist</div>
          <div className="text-xs text-muted-foreground">Use our packing checklist so nothing is forgotten in this bag.</div>
        </div>
        <Link to="/packing" data-testid="wallet-bag-open-packing"
          className="inline-flex items-center gap-1 text-sm text-indigo-700 font-700 hover:underline">
          Open Packing Buddy →
        </Link>
      </div>

      {form.id && <BagPhotoUploader bag={form} onChanged={onChanged} />}
      {form.id && (
        <label className="flex items-start gap-2 border border-indigo-200 bg-white p-3 rounded cursor-pointer" data-testid="wallet-bag-public-photo-toggle-row">
          <input type="checkbox" checked={!!form.public_photo_enabled}
            onChange={(e) => setForm({ ...form, public_photo_enabled: e.target.checked })}
            data-testid="wallet-bag-public-photo-toggle"
            className="mt-1 w-4 h-4 accent-indigo-600" />
          <span className="text-xs">
            <span className="font-700">Show the bag photo on the public return page</span> —
            airline lost-and-found staff can visually match at the belt. Only the first photo is shared; you can turn this off any time.
          </span>
        </label>
      )}

      <div className="flex gap-2">
        <button onClick={onSave} data-testid="wallet-bag-save"
          className="px-5 py-2.5 bg-indigo-600 text-white text-base font-700 hover:bg-indigo-700">Save</button>
        <button onClick={onCancel} data-testid="wallet-bag-close"
          className="px-4 py-2 border border-zinc-300 text-sm">Close</button>
      </div>
      {!form.id && <p className="text-xs text-indigo-800">Tip: save first — then you can attach photos of each bag &amp; tag.</p>}
    </div>
  );
}

/* Bag photo uploader — full bag photo(s) */
function BagPhotoUploader({ bag, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo must be under 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("kind", "bag");
      fd.append("caption", caption || "Bag photo");
      await api.post(`/wallet/bags/${bag.id}/photos`, fd);
      setCaption(""); if (fileRef.current) fileRef.current.value = "";
      onChanged?.(); toast.success("Bag photo saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-indigo-200 bg-white p-3 space-y-2" data-testid="wallet-bag-photo-uploader">
      <div className="flex items-center gap-2"><Camera className="w-5 h-5 text-indigo-700" /><div className="text-sm font-700">Add a photo of each bag</div></div>
      <p className="text-xs text-indigo-900">Airport lost-and-found teams love a clear side-on photo of the bag with its tag visible.</p>
      <TextIn placeholder="Caption (optional, e.g. 'Blue Samsonite')" value={caption}
        onChange={(e) => setCaption(e.target.value)} data-testid="wallet-bag-photo-caption" />
      <div className="flex gap-2">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={pick}
          className="hidden" id={`bag-photo-${bag.id}`} data-testid="wallet-bag-photo-file" />
        <label htmlFor={`bag-photo-${bag.id}`} data-testid="wallet-bag-photo-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-700 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Take / choose photo
        </label>
      </div>
    </div>
  );
}

/* Tag photo button — attaches to a specific tag_index */
function BagTagPhotoBtn({ bag, tagIndex, tag, onChanged, setForm }) {
  const [busy, setBusy] = useState(false);
  const inputId = `bag-tag-photo-${bag.id}-${tagIndex}`;
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo must be under 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("kind", "tag"); fd.append("tag_index", String(tagIndex));
      const { data } = await api.post(`/wallet/bags/${bag.id}/photos`, fd);
      // update local form state so preview shows without reload
      setForm((prev) => ({ ...prev, tags: prev.tags.map((t, i) => i === tagIndex ? { ...t, image_key: data.key } : t) }));
      onChanged?.(); toast.success("Tag photo saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const filename = tag.image_key ? tag.image_key.split("/").pop() : null;
  return (
    <>
      <input id={inputId} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick}
        data-testid={`wallet-bag-tag-photo-file-${tagIndex}`} />
      <label htmlFor={inputId} data-testid={`wallet-bag-tag-photo-btn-${tagIndex}`}
        className={`shrink-0 relative inline-flex items-center justify-center w-11 h-11 border-2 border-indigo-300 bg-indigo-50 cursor-pointer overflow-hidden ${busy ? "opacity-60 pointer-events-none" : ""}`}
        title="Attach tag photo">
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-indigo-600" /> :
          tag.image_key ? (
            <img src={`${api.defaults.baseURL}/wallet/bags/${bag.id}/photos/${filename}`}
              alt="tag" className="absolute inset-0 w-full h-full object-cover" />
          ) : <Camera className="w-4 h-4 text-indigo-600" />}
      </label>
    </>
  );
}

function BagPhotoStrip({ bag, onChanged }) {
  const photos = (bag.photos || []).filter((p) => typeof p === "object" && p.key);
  if (photos.length === 0) return null;
  const del = async (key) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/wallet/bags/${bag.id}/photos`, { params: { key } });
    onChanged?.();
  };
  const url = (key) => `${api.defaults.baseURL}/wallet/bags/${bag.id}/photos/${key.split("/").pop()}`;
  return (
    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid={`wallet-bag-photos-${bag.id}`}>
      {photos.map((p) => (
        <div key={p.key} className="relative group border border-zinc-200 bg-zinc-50 aspect-square overflow-hidden">
          <img src={url(p.key)} alt={p.caption || "Bag"} className="w-full h-full object-cover" />
          {p.caption && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">{p.caption}</div>}
          <button onClick={() => del(p.key)} data-testid={`wallet-bag-photo-del`}
            className="absolute top-1 right-1 bg-rose-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

